import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";

import { Button } from "@components/Button";
import { ButtonSize } from "@components/Button/types";
import { TextInput } from "@components/Input";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Routes } from "@ctypes";
import { useErrorBanner, useInviteToShareGoal } from "@hooks";
import { i18n, Translations } from "@i18n";
import { constants } from "@layout";
import { formatPhoneNumber, sanitizePhoneNumber } from "@utils";
import { validationSchema } from "./content";
import { ShareGoalContactRouteFormValues, ShareGoalContactRouteParams } from "./types";

export const ShareGoalContactRoute = () => {
  const [inviteToShareGoal, { loading: isSendingInvite }] = useInviteToShareGoal();

  const location = useLocation();
  const { showErrorBanner } = useErrorBanner();
  const navigate = useNavigate();

  const goalId = (location.state as ShareGoalContactRouteParams).goalId;

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<ShareGoalContactRouteFormValues>({
    defaultValues: {
      phoneNumber: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const phoneNumberController = register("phoneNumber");

  const onChangePhoneNumber = (value: string) => {
    const formattedPhoneNumber = formatPhoneNumber(value);

    setValue("phoneNumber", formattedPhoneNumber, constants.form);
  };

  const handleSubmit = async ({
    phoneNumber: phoneNumberValue,
  }: ShareGoalContactRouteFormValues) => {
    const phoneNumber = sanitizePhoneNumber(phoneNumberValue);

    try {
      await inviteToShareGoal({
        variables: {
          goalId,
          phoneNumber,
        },
      });

      navigate(Routes.ShareGoal, { replace: true, state: { goalId } });
    } catch {
      showErrorBanner();
    }
  };

  const props: MainLayoutProps = {
    pageHeader: i18n.t(Translations.ShareGoalContactRouteTitle),
  };

  return (
    <MainLayout {...props}>
      <TextInput
        {...phoneNumberController}
        label={i18n.t(Translations.ShareGoalContactRouteInputLabel)}
        placeholder={i18n.t(Translations.ShareGoalContactRouteInputPlaceholder)}
        value={watch("phoneNumber")}
        onChangeText={onChangePhoneNumber}
        error={errors.phoneNumber?.message}
      />
      <Spacing size={SpacingSizes.M} />
      <Button
        title={i18n.t(Translations.ShareGoalModalPrimaryCTA)}
        onClick={handleFormSubmit(handleSubmit)}
        size={ButtonSize.Small}
        loading={isSendingInvite}
        disabled={!isValid}
      />
    </MainLayout>
  );
};
